/**
 * Every public route path, in one place.
 *
 * This exists because renaming four routes to Swedish meant editing roughly
 * 300 string literals across 90 files — the paths had no single home, so
 * `/account/login` was spelled out wherever it was needed, including in
 * middleware, robots.txt and the protected-route list that must agree with
 * both. Three copies of the same string in three subsystems is how a route
 * ends up crawlable in one of them.
 *
 * The site is Swedish and its URLs now say so. `/jamfor` and `/angra-kop` were
 * already Swedish; `/account`, `/search`, `/cart` and `/checkout` were English
 * islands left over from the scaffold.
 *
 * ASCII only, no å/ä/ö — the convention `angra-kop` and `jamfor` already set.
 * A `ö` in a path is percent-encoded the moment it is shared or logged, so
 * `/sök` reads as `/s%C3%B6k` everywhere it matters.
 *
 * Renaming these after launch would mean a redirect layer on URLs Google had
 * just learned. Before switchover it costs nothing: none of these paths has
 * ever been public — staging sits behind deployment protection, and Magento
 * served entirely different names (`/customer/account`, `/catalogsearch/result`).
 * Those Magento spellings still need their robots entries; see app/robots.ts.
 */

export const ROUTES = {
  home: '/',
  search: '/sok',
  compare: '/jamfor',
  cart: '/varukorg',
  checkout: '/kassa',
  checkoutSuccess: '/kassa/tack',
  withdrawal: '/angra-kop',

  account: '/konto',
  accountLogin: '/konto/logga-in',
  accountRegister: '/konto/skapa-konto',
  accountForgotPassword: '/konto/glomt-losenord',
  accountResetPassword: '/konto/aterstall-losenord',
  accountProfile: '/konto/profil',
  accountAddresses: '/konto/adresser',
  accountLicense: '/konto/licens',
  accountMonitors: '/konto/bevakningar',
  accountOrders: '/konto/order',
  accountWishlist: '/konto/onskelista',
  accountNewsletter: '/konto/nyhetsbrev',
} as const;

/**
 * The account routes a signed-out visitor is allowed to reach.
 *
 * Kept apart from the rest of `/konto/` because the middleware must NOT bounce
 * them to the login page — that is the login page.
 */
export const AUTH_ROUTES = [
  ROUTES.accountLogin,
  ROUTES.accountRegister,
  ROUTES.accountForgotPassword,
  ROUTES.accountResetPassword,
] as const;

/**
 * Whether an unprefixed path is served by one of the app's own routes rather
 * than the `[...slug]` catch-all that renders Magento URLs. Home counts as own.
 */
export function isAppRoutePath(path: string): boolean {
  if (path === ROUTES.home) return true;
  return Object.values(ROUTES).some(
    (r) => r !== ROUTES.home && (path === r || path.startsWith(`${r}/`)),
  );
}

/** `/konto/...`, except the auth pages above. */
export function isProtectedPath(path: string): boolean {
  if (AUTH_ROUTES.some((r) => path === r || path.startsWith(`${r}/`))) return false;
  return path === ROUTES.account || path.startsWith(`${ROUTES.account}/`);
}
